import React, { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function SchedulePage() {
  const pageRef = useRef(null);
  const [activeDay, setActiveDay] = useState(1);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-text", { y: 40, opacity: 0, duration: 1, ease: "power3.out", stagger: 0.2 });
      gsap.utils.toArray(".animate-fade-up").forEach((section) => {
        gsap.fromTo(section, 
          { opacity: 0, y: 40 },
          { opacity: 1, y: 0, duration: 1, ease: "power2.out", scrollTrigger: { trigger: section, start: "top 85%" } }
        );
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  const scheduleData = {
    day1: {
      date: "14 January 2027",
      dayName: "Thursday",
      events: [
        {
          time: "10.00 - 11.00 hr",
          title: "Inauguration",
          type: "plenary"
        },
        {
          time: "11.00 - 11.45 hr",
          title: "Tea/Coffee & Networking Break",
          type: "break"
        },
        {
          time: "11.45 - 13.00 hr",
          title: "Plenary Session 1 : Road Safety Management Innovations",
          desc: "1 Moderator + 1 Keynote + 2 Speakers + 3 Panelists",
          type: "plenary"
        },
        {
          time: "13.00 - 14.00 hr",
          title: "Lunch & Networking Break",
          type: "break"
        },
        {
          time: "14.00 - 15.15 hr",
          title: "Plenary Session 2 : Development of Sustainable Infrastructure",
          desc: "1 Moderator + 1 Keynote + 2 Speakers + 3 Panelists",
          type: "plenary"
        },
        {
          time: "15.15 - 16.00 hr",
          title: "Tea/Coffee & Networking Break",
          type: "break"
        },
        {
          time: "16.00 - 17.15 hr",
          title: "Plenary Session 3 : Procurement & Contract – The Way Forward",
          desc: "1 Moderator + 1 Keynote + 2 Speakers + 3 Panelists",
          type: "plenary"
        },
        {
          time: "18.00 - 19.00 hr",
          title: "Cultural Programme",
          type: "networking"
        },
        {
          time: "19.00 - 21.30 hr",
          title: "Cocktail & Dinner",
          type: "networking"
        }
      ]
    },
    day2: {
      date: "15 January 2027",
      dayName: "Friday",
      events: [
        {
          time: "10.00 - 11.15 hr",
          title: "Plenary Session 4 : AI & Digitalization in Infrastructure",
          desc: "1 Moderator + 1 Keynote + 2 Speakers + 3 Panelists",
          type: "plenary"
        },
        {
          time: "11.15 - 12.00 hr",
          title: "Tea/Coffee & Networking Break",
          type: "break"
        },
        {
          time: "12.00 - 13.15 hr",
          title: "Plenary Session 5 : Smart Future of Mobility with AI",
          desc: "1 Moderator + 1 Keynote + 2 Speakers + 3 Panelists",
          type: "plenary"
        },
        {
          time: "13.15 - 14.00 hr",
          title: "Lunch & Networking Break",
          type: "break"
        },
        {
          time: "14.00 - 15.15 hr",
          title: "Parallel Sessions (Concurrent)",
          type: "track",
          parallelSessions: [
            { sessionNumber: "Parallel Session 1", name: "Safety in Mobility: Vision Zero" },
            { sessionNumber: "Parallel Session 2", name: "Modern Construction Equipment & Sustainable Materials" },
            { sessionNumber: "Parallel Session 3", name: "Digital Transformation in Infrastructure" }
          ]
        },
        {
          time: "15.15 - 16.00 hr",
          title: "Tea/Coffee & Networking Break",
          type: "break"
        },
        {
          time: "16.00 - 17.15 hr",
          title: "Parallel Sessions (Concurrent)",
          type: "track",
          parallelSessions: [
            { sessionNumber: "Parallel Session 4", name: "ITS & Smart Mobility" },
            { sessionNumber: "Parallel Session 5", name: "Procurement & Contract for Sustainability / Performance Based Contract for Sustainable Infra / CEO Session" },
            { sessionNumber: "Parallel Session 6", name: "Institutional Strengthening of Stakeholders" }
          ]
        },
        {
          time: "17.15 - 18.15 hr",
          title: "Awards & Valedictory Session",
          type: "plenary"
        }
      ]
    }
  };

  const currentSchedule = activeDay === 1 ? scheduleData.day1.events : scheduleData.day2.events;

  const getTypeStyle = (type) => {
    switch(type) {
      case 'plenary': return "bg-primary text-white";
      case 'track': return "bg-secondary text-white";
      case 'break': return "bg-slate-100 text-text/60 border border-slate-200";
      case 'networking': return "bg-accent text-white";
      default: return "bg-light text-primary";
    }
  };

  return (
    <main className="bg-light min-h-screen pb-20 font-sans" ref={pageRef}>
      
      {/* 🚀 REACT 19 NATIVE SEO HOISTING */}
      <title>Program Agenda & Summit Schedule | GRIS 2027</title>
      <meta name="description" content="Explore the official two-day program schedule agenda for GRIS 2027. Review technical tracking sessions, smart mobility forums, plenary roundtables, and leadership panels." />
      <meta name="keywords" content="GRIS 2027 Program Agenda, Summit Schedule New Delhi, Smart Mobility Tracks, Infra Event Plenary Sessions" />
      <meta property="og:title" content="Program Agenda & Summit Schedule | GRIS 2027" />
      <meta property="og:description" content="Two days of high-level innovations, strategic plenary sessions, and global transport networking at GRIS 2027." />

      {/* 🧠 RICH SNIPPETS */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Event",
            "name": "Global Road Infratech Summit (GRIS) 2027 Agenda Tracks",
            "startDate": "2027-01-14T10:00:00+05:30",
            "endDate": "2027-01-15T18:15:00+05:30",
            "location": {
              "@type": "Place",
              "name": "Ambedkar International Centre",
              "address": { "@type": "PostalAddress", "addressLocality": "New Delhi", "addressCountry": "IN" }
            }
          })
        }}
      />

      {/* 1. CINEMATIC HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-primary-dark">
        <div className="absolute inset-0 z-0">
          <img 
            src="/hero-road-2.jpg" 
            alt="GRIS 2027 Corporate Plenary Tracks and Technical Forums Infrastructure Layout" 
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover object-top opacity-30 mix-blend-luminosity" 
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-dark/80 via-primary-dark/90 to-light"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <span className="hero-text inline-block px-4 py-1.5 bg-accent/20 border border-accent/50 text-accent rounded-full text-xs font-bold uppercase tracking-widest mb-6 backdrop-blur-sm">
            Event Agenda
          </span>
          <h1 className="hero-text text-4xl md:text-5xl lg:text-7xl font-black text-white tracking-tight mb-6">
            Program <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-[#FFB067]">Schedule</span>
          </h1>
          <p className="hero-text mt-6 text-lg md:text-xl text-white/80 max-w-2xl mx-auto font-medium leading-relaxed">
            Two Days of Innovation, Strategic Insight, and Global Networking.
          </p>
        </div>
      </section>

      {/* 2. INTERACTIVE AGENDA SECTION */}
      <section className="py-16 lg:py-24 max-w-5xl mx-auto px-6 lg:px-8 -mt-20 relative z-20">
        
        <div role="tablist" aria-label="Summit Schedule Agenda Calendar Days" className="bg-white p-2 rounded-2xl shadow-premium border border-slate-100 flex justify-center mb-12 animate-fade-up">
          <button 
            role="tab"
            aria-selected={activeDay === 1}
            aria-label={`Show schedule matrix details for Summit Day 1 - ${scheduleData.day1.date}`}
            onClick={() => setActiveDay(1)}
            className={`flex-1 py-4 px-6 rounded-xl font-black text-sm lg:text-base uppercase tracking-widest transition-all duration-300 ${activeDay === 1 ? 'bg-primary text-white shadow-md' : 'text-text/50 hover:text-primary hover:bg-light'}`}
          >
            Day 01 <span className="hidden sm:inline font-medium capitalize text-xs ml-2 opacity-80">({scheduleData.day1.dayName}, {scheduleData.day1.date})</span>
          </button>
          <button 
            role="tab"
            aria-selected={activeDay === 2}
            aria-label={`Show schedule matrix details for Summit Day 2 - ${scheduleData.day2.date}`}
            onClick={() => setActiveDay(2)}
            className={`flex-1 py-4 px-6 rounded-xl font-black text-sm lg:text-base uppercase tracking-widest transition-all duration-300 ${activeDay === 2 ? 'bg-primary text-white shadow-md' : 'text-text/50 hover:text-primary hover:bg-light'}`}
          >
            Day 02 <span className="hidden sm:inline font-medium capitalize text-xs ml-2 opacity-80">({scheduleData.day2.dayName}, {scheduleData.day2.date})</span>
          </button>
        </div>

        {/* Timeline List */}
        <div className="space-y-6 animate-fade-up" style={{ animationDelay: '0.2s' }}>
          {currentSchedule.map((item, idx) => (
            <div key={idx} className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-slate-100 flex flex-col md:flex-row gap-6 lg:gap-10 items-start md:items-center hover:shadow-premium hover:-translate-y-1 transition-all duration-300 group">
              
              <div className="md:w-52 shrink-0 border-l-4 border-accent pl-4">
                <span className="text-xl lg:text-2xl font-black text-primary group-hover:text-accent transition-colors">
                  {item.time.split(" - ")[0]}
                </span>
                <br/>
                <span className="text-sm font-bold text-text/50">
                  {item.time.split(" - ")[1]}
                </span>
              </div>

              <div className="flex-grow w-full">
                <span className={`inline-block px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest mb-3 ${getTypeStyle(item.type)}`}>
                  {item.type}
                </span>
                <h3 className="text-xl font-bold text-primary mb-2">{item.title}</h3>
                {item.desc && <p className="text-sm text-text/60 leading-relaxed font-medium mb-3">{item.desc}</p>}

                {/* Parallel Tracks Grid for Day 2 */}
                {item.parallelSessions && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                    {item.parallelSessions.map((pSession, pIdx) => (
                      <div key={pIdx} className="bg-slate-50 border border-slate-200/80 rounded-xl p-4 flex flex-col justify-between">
                        <span className="text-[10px] font-black uppercase tracking-wider text-accent mb-1.5">
                          {pSession.sessionNumber}
                        </span>
                        <p className="text-xs font-bold text-primary leading-snug">
                          {pSession.name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
            </div>
          ))}
        </div>

        {/* Download PDF CTA */}
        <div className="mt-16 text-center animate-fade-up">
          <p className="text-text/50 text-sm font-bold uppercase tracking-widest mb-6">Need an offline copy?</p>
          <a href="/brochure" aria-label="Navigate directly to document prospectus brochure file layout downloader" className="inline-flex items-center justify-center px-10 py-4 bg-white border border-slate-200 text-primary font-black rounded-xl hover:bg-primary hover:text-white transition-colors shadow-sm">
            Download Full Program PDF
          </a>
        </div>

      </section>
    </main>
  );
}